// V1.5 Doctrines per docs/02_GAME_DESIGN.md §6. Mid-match tech-axis choice picked
// once at 3:00 from a 15-second window. Three options ship — all sidegrades.
// Default fallback is 'vanguard' if a player fails to pick before the window
// closes. Doctrines are deliberately not "purchaseable power" (Rule 11): the
// pick window is per-match, never tied to currency, profile, or unlocks.
//
// Effect plumbing lives in the system that owns the rule (Rule 12):
//   - Vanguard regen        → CombatSystem (after combat resolves; out-of-combat squads).
//   - Saboteur mark damage  → AbilitySystem (Mark Target apply path → CombatSystem damage).
//   - Bulwark turret range  → CombatSystem (turret acquisition + firing range).
//   - Bulwark hub build spd → BuildingSystem (production_hub buildProgress advancement).

export type DoctrineId = 'vanguard' | 'saboteur' | 'bulwark';

// Vanguard: 5% maxHp / second regen for own squads while out-of-combat.
//   Definition of "out of combat": Squad.target === null AND Squad.attackCooldown <=
//   the squad's full attackCooldownSec (i.e. the squad has no current target and
//   is not still cooling down from a recent shot). The CombatSystem updates target
//   each tick, so a squad that drops aggro this tick begins regenerating next tick.
export const VANGUARD_REGEN_PER_SEC = 0.05;

// Saboteur: Recon's Mark Target deals 5% of target.maxHp on the cast tick.
//   Routed through CombatSystem.applyDamage so Phase Shield absorbs and Core
//   armor scaling apply naturally if the target is a squad/core. (Mark Target
//   targets squads only in V1; the path is wired so future targets also work.)
export const SABOTEUR_MARK_DAMAGE_FRAC = 0.05;

// Bulwark: turret +25% range, hub +10% build speed. Both are owner-keyed lookups
// on the relevant building's ownerId — a squad/turret/hub belongs to its owner's
// chosen doctrine, not "all turrets get +25%".
export const BULWARK_TURRET_RANGE_MULT = 1.25;
export const BULWARK_HUB_BUILD_SPEED_MULT = 1.1;

// Pick window: opens at sim time DOCTRINE_PICK_WINDOW_OPEN_SEC, closes
// DOCTRINE_PICK_WINDOW_DURATION_SEC later. Players who haven't picked by
// the close tick get DEFAULT_DOCTRINE_ON_TIMEOUT.
//
// V1.7 (2.13): window extended 15 s → 30 s. The 15 s window was forcing
// players to pick under time pressure while still parsing what each
// doctrine actually meant.
//
// V1.8 — the SIM constants are kept at 180 / 30 because every bot AI
// and persistence flow keys off them. To address the CrazyGames-stats
// finding ("red-countdown modal at 3:00 catches players just as they
// bounce"), the CLIENT UI (DoctrinePicker.observeSnapshot) suppresses
// the modal on Match 1; the simulation still auto-assigns at the close
// tick via DEFAULT_DOCTRINE_ON_TIMEOUT so the player gets the doctrine
// effect without the modal nag on their first session.
export const DOCTRINE_PICK_WINDOW_OPEN_SEC = 180;
export const DOCTRINE_PICK_WINDOW_DURATION_SEC = 30;
export const DEFAULT_DOCTRINE_ON_TIMEOUT: DoctrineId = 'vanguard';

export interface DoctrineDef {
  id: DoctrineId;
  name: string;
  description: string;
}

// V1.7 (2.13) — outcome-led descriptions. The earlier numeric-jargon copy
// ("5% maxHp / sec regen", "Turrets +25% range") forced players to mentally
// translate stats into playstyle on the clock. Now each description leads
// with WHAT the playstyle FEELS like and what comp it enables, with the
// raw numbers in parentheses so power-users still see them.
export const DOCTRINE_DEFS: Readonly<Record<DoctrineId, DoctrineDef>> = Object.freeze({
  vanguard: Object.freeze({
    id: 'vanguard',
    name: 'Vanguard',
    description: `Aggressive play — your squads heal between fights. Hit, fall back to regen, push again without spending Signal on replacements. (${(VANGUARD_REGEN_PER_SEC * 100).toFixed(0)}%/sec out-of-combat regen.)`,
  }),
  saboteur: Object.freeze({
    id: 'saboteur',
    name: 'Saboteur',
    description: `Burst play — every Recon Mark also chunks the target's HP on cast. Stack Recons to bleed enemies before your main force arrives. (${(SABOTEUR_MARK_DAMAGE_FRAC * 100).toFixed(0)}% of max HP per mark.)`,
  }),
  bulwark: Object.freeze({
    id: 'bulwark',
    name: 'Bulwark',
    description: `Defensive play — your Turrets reach further and your Hubs come online faster. Lock chokepoints, out-produce the enemy. (Turrets +${((BULWARK_TURRET_RANGE_MULT - 1) * 100).toFixed(0)}% range; Hubs +${((BULWARK_HUB_BUILD_SPEED_MULT - 1) * 100).toFixed(0)}% build speed.)`,
  }),
});

export const DOCTRINE_IDS: readonly DoctrineId[] = ['vanguard', 'saboteur', 'bulwark'];
